<script lang="ts" setup>
	import {useFilters} from '@/store/filter.store';
	import {useFocus} from '@vueuse/core';
	import {computed, ref} from 'vue';
	import {useI18n} from 'vue-i18n';
	import {storeToRefs} from 'pinia';

	const emit = defineEmits(['search-updated']);

	const {t} = useI18n();
	const filterStore = useFilters();
	const minimumSearchLength = 2;
	const searchInput = ref();
	const {searchTerm} = storeToRefs(filterStore);
	const previousSearchTerm = ref(filterStore.searchTerm);
	const {focused: searchInputFocus} = useFocus(searchInput, {initialValue: false});

	const searchTermChanged = computed(() => searchTerm.value !== previousSearchTerm.value);
	const hasValue = computed(() => searchTerm.value?.length > 0);
	const isValidSearchTerm = computed(() => searchTerm.value && searchTerm.value.length >= minimumSearchLength);

	const clearInput = () => {
		if (!searchTerm.value) {
			return;
		}

		previousSearchTerm.value = '';
		filterStore.setSearchTerm('');
		emit('search-updated');
	};

	const handleSubmit = () => {
		// No submit if value is not empty or not valid or did not change.
		if ((hasValue.value && !isValidSearchTerm.value) || !searchTermChanged.value) {
			return;
		}

		previousSearchTerm.value = searchTerm.value;
		filterStore.setSearchTerm(searchTerm.value);
		emit('search-updated');
	};
</script>

<template>
	<div :class="{'show-catalog-search': searchInputFocus}" class="catalog-overview-header__search-container">
		<div class="catalog-overview-header__search-toggle">
			<label for="catalog-search">
				<svg class="icon-search" height="21" width="21">
					<use xlink:href="/assets/images/build/svg-sprite.min.svg#search"></use>
				</svg>
			</label>
		</div>

		<div class="catalog-overview-header__search">
			<form @submit.prevent="handleSubmit">
				<input id="catalog-search"
				       ref="searchInput"
				       v-model="searchTerm"
				       :placeholder="$t('site.products.overview.header.search.input.placeholder.text')"
				       :title="$t('site.products.overview.header.search.input.placeholder.text')"
				       autocomplete="off"
				       autocorrect="off"
				       class="catalog-search__input"
				       type="search" />

				<label class="catalog-search__submit" for="catalog-search">
					<svg class="icon-search" height="21" width="21">
						<use xlink:href="/assets/images/build/svg-sprite.min.svg#search"></use>
					</svg>
				</label>

				<button v-if="hasValue" class="catalog-search__reset" type="button" @click="clearInput">
					<svg class="icon-modal-close" height="20" width="20">
						<use xlink:href="/assets/images/build/svg-sprite.min.svg#modal-close"></use>
					</svg>
				</button>

				<div v-if="hasValue && searchTermChanged" class="search-suggestions search-suggestions--catalog">
					<div class="search-suggestions__enter">
						<span v-if="isValidSearchTerm" class="search-suggestions__enter__valid">
							{{ $t('site.products.overview.header.search.submit.title') }}
						</span>
						<span v-if="!isValidSearchTerm" class="search-suggestions__enter__invalid">
							{{
								t('site.products.overview.header.search.minimum.characters.required', [minimumSearchLength])
							}}
						</span>
					</div>
				</div>
			</form>
		</div>
	</div>
</template>
